import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { advanceReports, type AdvanceReportListItem } from '@/api/advanceReports';
import { refs } from '@/api/refs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdvanceReportsPage() {
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['advance-reports'],
    queryFn: () => advanceReports.list({ limit: 200 }),
  });
  const employeesQ = useQuery({
    queryKey: ['employees-all'],
    queryFn: () => refs.employees(),
  });

  const empName = useMemo(() => {
    const m = new Map<string, string>();
    employeesQ.data?.forEach((e) => m.set(e.id, e.name));
    return m;
  }, [employeesQ.data]);

  const cols = useMemo<ColumnDef<AdvanceReportListItem>[]>(() => [
    { key: 'document_number', header: '№', width: 150, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'document_date', header: 'Дата', width: 170,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
    },
    {
      key: 'employee_id', header: 'Підзвітна особа',
      sortValue: (r) => empName.get(r.employee_id) ?? '',
      cell: (r) => empName.get(r.employee_id) ?? '',
    },
    {
      key: 'total_amount', header: 'Сума', width: 140, align: 'right',
      sortValue: (r) => Number(r.total_amount),
      cell: (r) => fmt2(r.total_amount),
    },
    {
      key: 'posted', header: 'Проведено', width: 130, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0,
      cell: (r) => r.posted ? '✓ так' : 'ні',
    },
  ], [empName]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Авансові звіти</h2>
        <button className="primary" onClick={() => navigate('/advance-reports/new')}>
          + Новий АО
        </button>
      </div>
      <DataTable<AdvanceReportListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/advance-reports/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
